import './Plan.scss';
import { formatInTimeZone } from 'date-fns-tz';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Form } from 'react-bootstrap';
import { MdClose, MdEdit, MdOutlineFileDownload, MdRefresh } from 'react-icons/md';
import {
  abbreviateNumber,
  formatMoney,
  formatNumber,
  formatPercent
} from '@blisspointmedia/bpm-types/dist/formatUtils';
import {
  AgeRange,
  AudienceInsightsDimensions,
  parseAgeRangeExtrema
} from '@blisspointmedia/bpm-types/dist/YoutubePlanning';
import { Button } from '@Components/Button';
import { Appearance, Design, Placement, Size } from '@Components/designConstants';
import { IconButton } from '@Components/IconButton';
import { InfoTooltip } from '@Components/InfoTooltip';
import { FullPageSpinner } from '@Components/Spinner';
import { BPMTable, Header } from '@Components/StickyTable';
import { formatDateLabel } from '../../utils/date-utils';
import { downloadPNG } from '../../utils/download-utils';
import { useCompanyInfo } from '../../utils/hooks/useCompanyInfo';
import { useMap } from '../../utils/hooks/useData';
import { PoweredByGoogle } from './PoweredByGoogle';
import { SegmentGroup } from './SegmentGroup';
import { SpendVsChart } from './SpendVsChart';
import { ToplineNumber } from './ToplineNumber';
import {
  AudienceInsightMenuItem,
  AudienceInsightsDimensionLabels,
  YouTubePlanData
} from './youtube-planning-types';
import { parseNumberFromMoney } from './youtube-planning-utils';

interface PlanProps {
  planData?: YouTubePlanData;
  onClose: () => void;
  onRegenerate: (editedBudget, editedBudgetShare, editedInterests) => void;
  cid: string;
}

const AudienceInsightsDimensionDescriptions = {
  AFFINITY_USER_INTEREST: "Audiences based on users' passions, habits, and interests",
  IN_MARKET_USER_INTEREST: "Audiences based on users' recent purchase intent"
} as const;

export const PLAN_TABLE_HEADER_HEIGHT = 60;

const NoRows = () => <div>No rows to show.</div>;
export const PLAN_TABLE_BODY_HEIGHT = 140;

const toPrettyAgeRange = (ageRange: (keyof typeof AgeRange)[]): string => {
  const extrema = parseAgeRangeExtrema(ageRange);
  if (extrema == null) {
    return AgeRange.UNSPECIFIED;
  }
  const { min, max } = extrema;
  const strMin = min === 65 ? '' : `${min}-`;
  const strMax = max === 65 ? '65+' : max;
  return `${strMin}${strMax}`;
};

export const Plan = ({ planData, onClose, onRegenerate, cid }: PlanProps) => {
  const [regenerating, setRegenerating] = useState<boolean>(false);
  const [editMode, setEditMode] = useState<boolean>(false);
  const [editedBudget, setEditedBudget] = useState<number>(planData?.suggestedBudget || 0);
  const [editedBudgetShare, setEditedBudgetShareValue, setEditedBudgetShare] = useMap<
    string,
    number
  >(
    planData?.tableData != null
      ? Object.fromEntries(planData.tableData.map(row => [row.id, parseInt(row.budgetShare, 10)]))
      : {}
  );
  const [editedAffinityInterests, setEditedAffinityInterests] = useState<AudienceInsightMenuItem[]>(
    planData?.targetedSegments[AudienceInsightsDimensions.AFFINITY_USER_INTEREST] ?? []
  );
  const [editedMarketInterests, setEditedMarketInterests] = useState<AudienceInsightMenuItem[]>(
    planData?.targetedSegments[AudienceInsightsDimensions.IN_MARKET_USER_INTEREST] ?? []
  );

  const { fiscal_cal: fiscalCal } = useCompanyInfo();

  // The editedBudget useState initializer only runs on first mount, so a new plan arriving via
  // Regenerate (new planData, new suggestedBudget) would otherwise leave editedBudget stale.
  // Re-sync it whenever the suggested budget changes.
  useEffect(() => {
    setEditedBudget(planData?.suggestedBudget || 0);
  }, [planData?.suggestedBudget]);

  const balanceBudgetShares = useCallback(
    (rowNumber, newBudgetShare) => {
      setEditedBudgetShareValue(rowNumber, newBudgetShare);
      const otherRow = 3 - parseInt(rowNumber, 10); // 2 -> 1, 1 -> 2
      setEditedBudgetShareValue(otherRow.toString(), 100 - newBudgetShare);
    },
    [setEditedBudgetShareValue]
  );

  const regeneratePlan = useCallback(async () => {
    setEditMode(false);
    setRegenerating(true);
    await onRegenerate(editedBudget, editedBudgetShare, {
      [AudienceInsightsDimensions.AFFINITY_USER_INTEREST]: editedAffinityInterests,
      [AudienceInsightsDimensions.IN_MARKET_USER_INTEREST]: editedMarketInterests
    });
    setRegenerating(false);
  }, [
    editedBudget,
    editedBudgetShare,
    onRegenerate,
    editedAffinityInterests,
    editedMarketInterests
  ]);

  const downloadPlan = useCallback(() => {
    const today = formatInTimeZone(new Date(), 'America/New_York', 'yyyy-MM-dd');
    downloadPNG('.youtube-plan', [cid, 'youtube_plan', today], '', [], true, false, true);
  }, [cid]);

  const dimensionToSegments = useMemo(
    () => ({
      [AudienceInsightsDimensions.AFFINITY_USER_INTEREST]: {
        segmentArr: editedAffinityInterests,
        setter: setEditedAffinityInterests
      },
      [AudienceInsightsDimensions.IN_MARKET_USER_INTEREST]: {
        segmentArr: editedMarketInterests,
        setter: setEditedMarketInterests
      }
    }),
    [editedAffinityInterests, editedMarketInterests]
  );

  const onSegmentDelete = useCallback(
    (dimension: AudienceInsightsDimensions, id: string) => {
      dimensionToSegments[dimension].setter(prev => prev.filter(segment => segment.value !== id));
    },
    [dimensionToSegments]
  );

  const onCancel = useCallback(() => {
    setEditMode(false);
    setEditedBudget(planData?.suggestedBudget || 0);
    setEditedBudgetShare({});
    setEditedAffinityInterests(
      planData?.targetedSegments[AudienceInsightsDimensions.AFFINITY_USER_INTEREST] ?? []
    );
    setEditedMarketInterests(
      planData?.targetedSegments[AudienceInsightsDimensions.IN_MARKET_USER_INTEREST] ?? []
    );
  }, [
    planData?.targetedSegments,
    planData?.suggestedBudget,
    setEditedBudgetShare,
    setEditedAffinityInterests,
    setEditedMarketInterests
  ]);

  const percentageReach = useMemo(() => {
    const { onTargetReach, youTubePopulation } = planData ?? {};

    if (typeof onTargetReach === 'number' && youTubePopulation) {
      return onTargetReach / youTubePopulation;
    }
    return undefined;
  }, [planData]);

  const planTableRowHeight =
    planData?.tableData == null ? 0 : PLAN_TABLE_BODY_HEIGHT / planData.tableData.length;

  const planTableHeight =
    planData?.tableData == null
      ? 0
      : PLAN_TABLE_HEADER_HEIGHT + planData.tableData.length * planTableRowHeight;

  const tableContainerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (tableContainerRef.current) {
      tableContainerRef.current.style.setProperty('--plan-table-height', `${planTableHeight}px`);
    }
  }, [planTableHeight]);

  const planTableHeaders: Header[] = useMemo(
    () => [
      {
        label: 'Format',
        name: 'format',
        width: 228,
        renderer: value => (
          <div className='format-cell'>
            {value.format}
            {/* To connect with youtube-plan-table-footer */}
            {value.format === 'Bumpers' ? <span className='asterisk'>*</span> : ''}
          </div>
        )
      },
      {
        label: 'Budget',
        name: 'budget',
        flex: 1,
        renderer:
          editMode && planData && planData.tableData.length > 1
            ? value => {
                const editedBudgetVal =
                  editedBudgetShare[value.id] !== undefined
                    ? (editedBudgetShare[value.id]! * editedBudget) / 100
                    : undefined;
                return formatMoney(editedBudgetVal !== undefined ? editedBudgetVal : value.budget);
              }
            : value => formatMoney(parseFloat(value.budget.replace(/\$/g, '').replace(/,/g, '')))
      },
      {
        label: 'Budget Share',
        name: 'budgetShare',
        flex: 1,
        renderer:
          editMode && planData && planData.tableData.length > 1
            ? value => (
                <Form.Control
                  type='number'
                  className='budget-share-input'
                  value={
                    editedBudgetShare[value.id] == null
                      ? value.budgetShare.replace('%', '')
                      : editedBudgetShare[value.id]
                  }
                  onChange={e =>
                    balanceBudgetShares(value.id, (e.target as HTMLInputElement).valueAsNumber)
                  }
                  onWheel={e => e.currentTarget.blur()}
                />
              )
            : value => value.budgetShare
      },
      {
        label: 'Reach',
        name: 'reach',
        flex: 1
      },
      {
        label: 'Avg. Freq.',
        name: 'avgFreq',
        flex: 1
      },
      {
        label: 'Cost Per Reach',
        name: 'costPerReach',
        flex: 1
      },
      {
        label: 'Imps',
        name: 'imps',
        flex: 1
      },
      {
        label: 'CPM',
        name: 'cpm',
        flex: 1
      },
      {
        label: 'Views',
        name: 'views',
        flex: 1,
        renderer: value => (value.format === 'Bumpers' ? 'N/A' : value.views)
      },
      {
        label: 'CPV',
        name: 'cpv',
        flex: 1
      }
    ],
    [editMode, planData, editedBudget, editedBudgetShare, balanceBudgetShares]
  );

  return (
    <div className='youtube-plan'>
      {regenerating ? (
        <FullPageSpinner />
      ) : (
        <div>
          <div className='youtube-plan-header'>
            <div className='youtube-plan-header-text-container'>
              <div className='youtube-plan-header-text'>
                Recommendations and Estimates <PoweredByGoogle />
              </div>
              <div className='youtube-plan-header-subtext'>
                {`${planData?.goal}, ${formatMoney(
                  planData?.inputBudget || 0,
                  0
                )} Budget, Audience Interest: "${planData?.queryString.trim()}",  ${formatDateLabel(
                  planData?.dates.start || '',
                  fiscalCal
                )} - ${formatDateLabel(planData?.dates.end || '', fiscalCal)}`}
              </div>
              <div className='youtube-plan-header-tertiary-text'>
                {`Additional Targeting - Gender: ${planData?.gender}, Age: ${
                  planData != null ? toPrettyAgeRange(planData.ageRange) : 'Undefined'
                }, States: ${planData?.targetLocation.map(loc => loc.name).join(', ')}`}
              </div>
            </div>
            <div className='youtube-plan-header-controls'>
              {editMode && (
                <Button
                  size={Size.LG}
                  className='youtube-plan-edit-button'
                  appearance={Appearance.OUTLINED}
                  onClick={onCancel}
                >
                  Cancel
                </Button>
              )}
              {editMode && (
                <Button
                  size={Size.LG}
                  className='youtube-plan-edit-button'
                  appearance={Appearance.OUTLINED}
                  onClick={regeneratePlan}
                  trailingElement={<MdRefresh />}
                >
                  Regenerate
                </Button>
              )}
              {!editMode && (
                <Button
                  size={Size.LG}
                  className='youtube-plan-edit-button'
                  appearance={Appearance.OUTLINED}
                  onClick={() => setEditMode(true)}
                  trailingElement={<MdEdit />}
                >
                  Edit Inputs
                </Button>
              )}
              <IconButton className='youtube-plan-download-button' onClick={downloadPlan}>
                <MdOutlineFileDownload />
              </IconButton>
              <IconButton
                className='youtube-plan-close-button'
                appearance={Appearance.EMPTY}
                design={Design.NEUTRAL}
                onClick={onClose}
              >
                <MdClose />
              </IconButton>
            </div>
          </div>
          <div className='youtube-plan-topline-numbers-container'>
            <ToplineNumber
              label={
                <div className='label-with-tooltip'>
                  Budget
                  <InfoTooltip size={Size.SM} placement={Placement.TOP_END}>
                    The budget shown might not exactly match your input because Google selects the
                    closest available estimate to your desired amount.
                  </InfoTooltip>
                </div>
              }
              value={planData?.suggestedBudget}
              formatter={formatMoney}
              editable={editMode}
              onEdit={setEditedBudget}
              onEditParams={{
                valueFormatter: (value: number) => `$${value.toLocaleString('en-US')}`,
                valueParser: (currencyStr: string) => parseNumberFromMoney(currencyStr)
              }}
            />
            <ToplineNumber
              label='On Target Reach'
              value={planData?.onTargetReach}
              formatter={formatNumber}
            />
            <ToplineNumber
              label='Avg. Frequency'
              value={planData?.averageFreq}
              formatter={freq => formatNumber(freq, 2)}
            />
            <ToplineNumber label='CPM' value={planData?.cpm} formatter={formatMoney} />
            <ToplineNumber
              label={
                <div className='label-with-tooltip'>
                  YouTube Population
                  <InfoTooltip size={Size.SM} placement={Placement.TOP_END}>
                    The YouTube population is only available for National plans. State-targeted
                    plans will display N/A.
                  </InfoTooltip>
                </div>
              }
              value={planData?.youTubePopulation}
              formatter={planData?.youTubePopulation ? abbreviateNumber : () => 'N/A'}
            />
            <ToplineNumber
              label={
                <div className='label-with-tooltip'>
                  % Reach
                  <InfoTooltip size={Size.SM} placement={Placement.TOP_END}>
                    The YouTube % Reach statistic is only available for National plans.
                    State-targeted plans will display N/A.
                  </InfoTooltip>
                </div>
              }
              value={percentageReach}
              formatter={value => {
                if (!value) {
                  return 'N/A';
                }
                const cappedValue = Math.min(value, 1);
                return formatPercent(cappedValue, 2);
              }}
            />
          </div>
          <div className='youtube-plan-spend-charts-container'>
            <SpendVsChart
              variableName='Reach'
              data={planData?.spendVsReach || []}
              suggestedBudget={planData?.suggestedBudget}
            />
            <SpendVsChart
              variableName='Views'
              data={planData?.spendVsViews || []}
              suggestedBudget={planData?.suggestedBudget}
            />
          </div>
          <div className='youtube-plan-targeted-segments-container'>
            <div className='container-header'>Audience Segments</div>
            <div className='container-body'>
              {Object.keys(dimensionToSegments).map(key => (
                <SegmentGroup
                  key={key}
                  title={AudienceInsightsDimensionLabels[key]}
                  description={AudienceInsightsDimensionDescriptions[key]}
                  masterSegments={planData?.masterSegments[key]}
                  targetedSegments={dimensionToSegments[key].segmentArr}
                  editMode={editMode}
                  setTargetedSegments={dimensionToSegments[key].setter}
                  onTargetedSegmentDelete={onSegmentDelete}
                />
              ))}
            </div>
          </div>
          <div className='youtube-plan-table-container' ref={tableContainerRef}>
            <BPMTable
              className='youtube-plan-table'
              alternateColors={false}
              data={planData?.tableData}
              headers={planTableHeaders}
              filterBar={false}
              noRowsRenderer={NoRows}
              rowHeight={planTableRowHeight}
              headerHeight={PLAN_TABLE_HEADER_HEIGHT}
            />
          </div>
          <div className='youtube-plan-table-footer'>
            *Non-skippable formats such as Bumpers don&apos;t produce views or view metrics. These
            formats use CPM bidding exclusively.
          </div>
        </div>
      )}
    </div>
  );
};
